import {
  DataLoaderJobType,
  DataLoaderState,
  DataLoaderStatus,
  CGDOLoadingResponse,
  DataLoaderJobState,
} from 'app/modules/investigations/types/responses';
import { FullEntityResponse } from 'app/modules/entities/responses';
import { FullInstrumentResponse } from 'app/modules/instruments/responses';
import { FullTxnResponse } from 'app/modules/transactions/types/responses';
import {
  CachedActionEventResponse,
  ShortActionEventResponse,
} from 'app/modules/actionEvents/responses';

export enum ColdStorageLoadingStatus {
  LOADED = 'LOADED',
  LOADING = 'LOADING',
  SLOW_LOADING = 'SLOW_LOADING', // Still loading, past when the FE would have already retried once
  LOADING_TIMEOUT = 'LOADING_TIMEOUT', // We are still loading but it's been more than the threshold
}

// See docstring of enum in Umbridge for more details
const BACKEND_IN_PROGRESS_STATUSES: readonly DataLoaderStatus[] = [
  DataLoaderStatus.TO_QUEUE,
  DataLoaderStatus.QUEUED,
  DataLoaderStatus.LOADING,
] as const;

export const LOADING_TIME_THRESHOLD_IN_SECONDS = 7.5 * 60; // 7.5 minutes
export const COLD_STORAGE_WAIT_TIME = 30 * 1000; // 30 seconds refetch interval to start
export const COLD_STORAGE_MAX_RETRIES = 6;

// Exponential backoff for cold storage based on retry count
// More frequent polling early on, then exponential backoff
export const getColdStorageRetryWaitTime = (retryCount: number): number => {
  // Early retries: 5s, 10s, 20s for faster initial polling
  if (retryCount === 0) return 5 * 1000; // 5 seconds
  if (retryCount === 1) return 10 * 1000; // 10 seconds
  if (retryCount === 2) return 20 * 1000; // 20 seconds
  // Then exponential backoff: 80s, 160s, 320s, etc.
  return 2 ** (retryCount - 1) * 20 * 1000;
};

// Once a job has been loading longer than the FE's first retry would have
// taken, surface a distinct status so callers can optionally communicate
// that we're still looking for data before hitting the full
// LOADING_TIME_THRESHOLD_IN_SECONDS.
export const SLOW_LOADING_THRESHOLD_IN_SECONDS =
  getColdStorageRetryWaitTime(0) / 1000;

const resolveColdStorageLoadingStatus = (
  secondsSinceExecution: number,
): ColdStorageLoadingStatus => {
  if (secondsSinceExecution < SLOW_LOADING_THRESHOLD_IN_SECONDS) {
    return ColdStorageLoadingStatus.LOADING;
  }
  if (secondsSinceExecution < LOADING_TIME_THRESHOLD_IN_SECONDS) {
    return ColdStorageLoadingStatus.SLOW_LOADING;
  }
  return ColdStorageLoadingStatus.LOADING_TIMEOUT;
};

export const getLoadingFromColdStorageState = (
  articleId: number,
  beState:
    | DataLoaderState<'ALERT'>
    | DataLoaderState<'CASE'>
    | DataLoaderState<'SAR_FILING'>
    | undefined,
): {
  [key in DataLoaderJobType]: ColdStorageLoadingStatus;
} => {
  const loadingState: {
    [key in DataLoaderJobType]: ColdStorageLoadingStatus;
  } = {
    txn_event: ColdStorageLoadingStatus.LOADED,
    instrument: ColdStorageLoadingStatus.LOADED,
    action_event: ColdStorageLoadingStatus.LOADED,
    entity: ColdStorageLoadingStatus.LOADED,
    entity_txn_event_assoc: ColdStorageLoadingStatus.LOADED,
    instrument_txn_event_assoc: ColdStorageLoadingStatus.LOADED,
  };

  const alertBEState = beState?.article_ids[articleId];
  if (alertBEState) {
    Object.keys(alertBEState).forEach((key: DataLoaderJobType) => {
      if (
        alertBEState[key].seconds_since_execution !== null &&
        BACKEND_IN_PROGRESS_STATUSES.includes(alertBEState[key].status)
      ) {
        // Escalate the loading status the longer the job has been running
        loadingState[key] = resolveColdStorageLoadingStatus(
          alertBEState[key].seconds_since_execution,
        );
      }
    });
  }
  return loadingState;
};

export const isColdStorageLoading = (
  status: ColdStorageLoadingStatus | null,
): boolean =>
  status === ColdStorageLoadingStatus.LOADING ||
  status === ColdStorageLoadingStatus.SLOW_LOADING;

export const hasAnyCGDOsLoading = (
  loadingState:
    | {
        [key in DataLoaderJobType]: DataLoaderJobState;
      }
    | {
        [key in DataLoaderJobType]: ColdStorageLoadingStatus;
      }
    | undefined,
  includeLoadingTimeout = true,
) => {
  /**
   * The method receives the state from the BE or the FE transformed state with the loading_timeout included
   */
  return Object.values(loadingState || {}).some(
    (state: DataLoaderJobState | ColdStorageLoadingStatus) => {
      if (typeof state === 'string') {
        const isLoading =
          state === ColdStorageLoadingStatus.LOADING ||
          state === ColdStorageLoadingStatus.SLOW_LOADING;
        if (includeLoadingTimeout) {
          return (
            isLoading || state === ColdStorageLoadingStatus.LOADING_TIMEOUT
          );
        }
        return isLoading;
      }
      // We have the BE state, just check if it's loading
      return BACKEND_IN_PROGRESS_STATUSES.includes(state.status);
    },
  );
};

export const getLoadingFromColdStorageStateForCGDO = (
  cgdoHashedId: string,
  beState: CGDOLoadingResponse['data_loader'] | undefined,
): ColdStorageLoadingStatus => {
  const objectState = beState?.[cgdoHashedId];
  if (
    objectState &&
    objectState.seconds_since_execution !== null &&
    BACKEND_IN_PROGRESS_STATUSES.includes(objectState.status)
  ) {
    return resolveColdStorageLoadingStatus(objectState.seconds_since_execution);
  }
  return ColdStorageLoadingStatus.LOADED;
};
// Typescript guard to know if the data is loading or not
export const isCGDOLoadingResponse = (
  data: object | undefined,
): data is CGDOLoadingResponse => {
  if (data === undefined || !('data_loader' in data)) {
    return false;
  }
  // Check if data_loader has the CGDOLoadingResponse structure (flat hash map)
  // vs DataLoaderState structure (has article_type and article_ids)
  const loader = (data as { data_loader: unknown }).data_loader;
  return (
    typeof loader === 'object' && loader !== null && !('article_type' in loader)
  );
};

export function isArticleLoadingResponse<
  T extends 'ALERT' | 'CASE' | 'SAR_FILING',
>(
  data: object | undefined,
  articleType: T,
): data is { data_loader: DataLoaderState<T> } {
  if (data === undefined || !('data_loader' in data)) {
    return false;
  }

  // Check if data_loader has the DataLoaderState structure (has article_type and article_ids)
  // vs CGDOLoadingResponse structure (flat hash map)
  const loader = (data as { data_loader: unknown }).data_loader;
  return (
    typeof loader === 'object' &&
    loader !== null &&
    'article_type' in loader &&
    'article_ids' in loader &&
    (loader as { article_type: unknown }).article_type === articleType
  );
}
export const hasNoLoadedData = (data: CGDOLoadingResponse): boolean => {
  for (const [, jobState] of Object.entries(data.data_loader)) {
    if (jobState.status === 'LOADED') {
      return false;
    }
  }
  return true;
};

/**
 * Type guard to check if data is a FullEntityResponse (including intersection types).
 * Returns true if the data contains entity properties (id or external_id).
 */
export function isEntityResponse(
  data:
    | FullEntityResponse
    | (FullEntityResponse & CGDOLoadingResponse)
    | CGDOLoadingResponse,
): data is FullEntityResponse | (FullEntityResponse & CGDOLoadingResponse) {
  return 'id' in data || 'external_id' in data;
}

/**
 * Type guard to check if data is a FullInstrumentResponse (including intersection types).
 * Returns true if the data contains instrument properties (id or external_id).
 */
export function isInstrumentResponse(
  data:
    | FullInstrumentResponse
    | (FullInstrumentResponse & CGDOLoadingResponse)
    | CGDOLoadingResponse,
): data is
  | FullInstrumentResponse
  | (FullInstrumentResponse & CGDOLoadingResponse) {
  return 'id' in data || 'external_id' in data;
}

/**
 * Type guard to check if data is a FullTxnResponse (including intersection types).
 * Returns true if the data contains transaction properties (id or external_id).
 */
export function isTransactionResponse(
  data:
    | FullTxnResponse
    | (FullTxnResponse & CGDOLoadingResponse)
    | CGDOLoadingResponse,
): data is FullTxnResponse | (FullTxnResponse & CGDOLoadingResponse) {
  return 'id' in data || 'external_id' in data;
}

/**
 * Type guard to check if data is an ActionEventResponse (including intersection types).
 * Returns true if the data contains action event properties (id or external_id).
 */
export function isActionEventResponse(
  data:
    | CachedActionEventResponse
    | ShortActionEventResponse
    | (CachedActionEventResponse & CGDOLoadingResponse)
    | CGDOLoadingResponse,
): data is
  | CachedActionEventResponse
  | ShortActionEventResponse
  | ((CachedActionEventResponse | ShortActionEventResponse) &
      CGDOLoadingResponse) {
  return 'id' in data || 'external_id' in data;
}

/**
 * Type guard factory to check if CGDO loading response contains actual data.
 * This is a generic version that works for any CGDO type.
 */
export function hasCGDOData<
  T extends { id?: string | number; external_id?: string },
>(
  data: T | (T & CGDOLoadingResponse) | CGDOLoadingResponse | undefined,
): data is T & CGDOLoadingResponse {
  return isCGDOLoadingResponse(data) && ('id' in data || 'external_id' in data);
}
